/**
 * Surface kinetics - the wafer-scale half of the etch model.
 *
 * Etching here is a heterogeneous reaction: it proceeds only on exposed film,
 * at a rate set by whichever supply is scarcer - the neutral etchant arriving by
 * transport, or the ion bombardment that makes the surface reactive. That
 * competition is the whole point. The loading effect and the centre-to-edge
 * signature a technician reads off a thickness map are consequences of the
 * scarcer-supply cap, not behaviours written into the model.
 */

import { F_PER_SI, FILM_DENSITY, R_GAS } from './constants'
import { clamp } from '../core/math'

export interface SurfaceInputs {
  /** Local etchant impingement flux [m^-2 s^-1]. */
  neutralFlux: number
  /** Local ion flux [m^-2 s^-1]. */
  ionFlux: number
  /** Mean ion energy [eV]. */
  ionEnergyEv: number
  /** Wafer surface temperature [K]. */
  surfaceTempK: number
}

export interface SurfaceResult {
  /** Silicon etch rate [m/s]. */
  etchRate: number
  /** Etchant consumed per unit area [m^-2 s^-1] - the sink the reactor sees. */
  etchantConsumption: number
  /** Rate contributed by spontaneous (thermal) chemistry [m/s]. */
  chemicalRate: number
  /** Rate contributed by ion-assisted chemistry [m/s]. */
  ionAssistedRate: number
  /** Oxide etch rate [m/s], for selectivity. */
  oxideRate: number
  /** Si:SiO2 selectivity. */
  selectivity: number
  /**
   * Damkoehler-like number: reaction demand over transport supply. Below ~0.3
   * the etch is reaction-limited and uniform; above ~1 it is transport-limited
   * and the wafer edge, which sees fresher gas, runs fast.
   */
  damkohler: number
}

/**
 * Activation energy for spontaneous F + Si [J/mol]. The measured value for
 * fluorine on silicon is close to 0.108 eV; this is that, in molar units.
 */
export const EA_SI_F = 10_400

/**
 * Reaction probability prefactor for spontaneous F on Si (dimensionless).
 * Fluorine etches bare silicon with no ion help at all - this is why XeF2 works
 * as a dry release etchant - so this branch is a substantial fraction of the
 * total at nominal bias, and it is what gives wafer temperature a visible
 * effect and silicon its selectivity over oxide.
 */
const GAMMA0_SI = 1.2

/**
 * Threshold energies for ion-assisted removal [eV]. Oxide needs a much harder
 * ion because there is no spontaneous channel to assist: the Si-O bond has to
 * be broken by the impact itself.
 */
export const E_THRESHOLD_SI = 16
const E_THRESHOLD_OXIDE = 50

/** Ion-assisted yield coefficients [atoms per ion per sqrt(eV)]. */
export const YIELD_COEFF_SI = 0.22
const YIELD_COEFF_OXIDE = 0.05

/**
 * SiO2 + 4F -> SiF4 + O2. Same fluorine budget per silicon atom as bare Si;
 * getting this wrong makes oxide appear to etch faster than silicon in the
 * neutral-limited regime, which would invert the whole selectivity lesson.
 */
const F_PER_OXIDE = 4

/**
 * Single calibration constant of the etch model.
 *
 * Every dependence in this file is physical - Arrhenius in temperature, square
 * root above threshold in ion energy, scarcer-supply competition between the two
 * fluxes. This prefactor sets the absolute scale, and is fitted so the qualified
 * baseline recipe (20 mTorr, 600 W source, 100 W bias, 40 C, 35% open area)
 * lands near 250 nm/min - a representative number for a production silicon etch.
 *
 * Fidelity standard, per the project design: correct qualitative behaviour,
 * correct direction and approximate magnitude of response to every knob the
 * technician controls, correct coupling between scales. This constant is where
 * absolute accuracy is knowingly traded away to get the other three.
 */
export const ETCH_CALIBRATION = 1.3

/**
 * Ion-enhanced yield above threshold. Sputtering and ion-assisted chemistry both
 * follow a square-root-above-threshold law over the energy range that matters
 * here (roughly 20-500 eV).
 */
export function ionYield(energyEv: number, thresholdEv: number, coeff: number): number {
  if (energyEv <= thresholdEv) return 0
  return coeff * (Math.sqrt(energyEv) - Math.sqrt(thresholdEv))
}

/**
 * Evaluate the surface at one point on the wafer.
 *
 * The ion-assisted branch uses the standard competition form
 *
 *     R = (A * B) / (A + B)
 *
 * where A is the neutral supply and B is the rate at which ions create reactive
 * sites. This is a harmonic mean: it tends to whichever term is smaller, so the
 * etch is neutral-starved when the gas cannot keep up and ion-starved when the
 * bombardment cannot. Both limits are things a technician has to recognise.
 */
export function solveSurface(inp: SurfaceInputs): SurfaceResult {
  const { neutralFlux, ionFlux, ionEnergyEv, surfaceTempK } = inp

  // Spontaneous branch: thermal fluorine chemistry, Arrhenius in wafer temp.
  const gammaThermal = GAMMA0_SI * Math.exp(-EA_SI_F / (R_GAS * Math.max(surfaceTempK, 1)))
  const chemicalConsumption = neutralFlux * gammaThermal

  // Ion-assisted branch: ions create reactive sites, neutrals fill them.
  const ySi = ionYield(ionEnergyEv, E_THRESHOLD_SI, YIELD_COEFF_SI)
  const siteGeneration = ionFlux * ySi * F_PER_SI
  const ionAssistedConsumption =
    siteGeneration > 0 && neutralFlux > 0
      ? (neutralFlux * siteGeneration) / (neutralFlux + siteGeneration)
      : 0

  const etchantConsumption = ETCH_CALIBRATION * (chemicalConsumption + ionAssistedConsumption)

  const perSiAtom = F_PER_SI * FILM_DENSITY.Si
  const chemicalRate = (ETCH_CALIBRATION * chemicalConsumption) / perSiAtom
  const ionAssistedRate = (ETCH_CALIBRATION * ionAssistedConsumption) / perSiAtom
  const etchRate = chemicalRate + ionAssistedRate

  // Oxide: essentially no spontaneous branch, and a much higher ion threshold.
  // Selectivity therefore collapses as bias power rises - the reason a
  // technician cannot simply turn up the bias to hit a rate target.
  const yOx = ionYield(ionEnergyEv, E_THRESHOLD_OXIDE, YIELD_COEFF_OXIDE)
  const oxideSites = ionFlux * yOx * F_PER_OXIDE
  const oxideConsumption =
    oxideSites > 0 && neutralFlux > 0
      ? (neutralFlux * oxideSites) / (neutralFlux + oxideSites)
      : 0
  const oxideRate = (ETCH_CALIBRATION * oxideConsumption) / (F_PER_OXIDE * FILM_DENSITY.SiO2)

  const selectivity = oxideRate > 1e-14 ? etchRate / oxideRate : 999

  // Demand over supply. siteGeneration is what the surface could consume if
  // neutrals were unlimited; neutralFlux is what transport actually delivers.
  const damkohler = neutralFlux > 0 ? siteGeneration / neutralFlux : 0

  return {
    etchRate,
    etchantConsumption,
    chemicalRate,
    ionAssistedRate,
    oxideRate,
    selectivity,
    damkohler: clamp(damkohler, 0, 1e6),
  }
}

/**
 * Sidewall angle from the balance between directional ion flux and isotropic
 * neutral chemistry. A profile that is all chemistry undercuts the mask; one
 * that is ion-driven runs vertical. Reported in degrees from horizontal, so 90
 * is a perfectly anisotropic profile.
 */
export function profileAngleDeg(res: SurfaceResult): number {
  const total = res.chemicalRate + res.ionAssistedRate
  if (total <= 0) return 90
  const anisotropy = res.ionAssistedRate / total
  return 90 - 42 * (1 - anisotropy) ** 1.3
}

/** Convert m/s to the nm/min a tool actually displays. */
export const toNmPerMin = (metresPerSecond: number) => metresPerSecond * 1e9 * 60
